import { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { CheckCircle } from 'lucide-react';
import { Button, TextArea } from '~/components/ui';
import { SectionPanel } from '~/components/ui/SectionPanel';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';
import {
  MagicLinkStatusCard,
  TokenErrorView,
} from '~/components/magic-link/MagicLinkStatusCard';
import { useTokenLoadedOnce } from '~/hooks/useTokenLoadedOnce';
import {
  getByToken,
  postByToken,
  tokenErrorCodeOf,
  type TokenErrorCode,
} from '~/lib/magicLinkToken';
import {
  QuoteRoundParty,
  UnitCode,
  type Currency,
  type Money,
  type MoneyInput,
  type QuoteStatus,
} from '~/graphql/graphql';
import {
  buildProductGroups,
  type GroupableItem,
} from '~/pages/rfq-view/itemGroups';
import { CounterQuoteForm } from '~/pages/rfq-view/sections/CounterQuoteAction';
import type { CounterSubmission } from '~/pages/rfq-view/sections/useSendCounter';
import { moneyOrDash } from '~/pages/rfq-view/sections/quoteDisplay';
import { formatQuantityForUnit } from '~/utils';
import {
  isDeclinable,
  type CounterableQuote,
} from '~/pages/rfq-view/sections/quoteNegotiation';

const NAMESPACE = 'answer-quote-counter';
const KEY_PREFIX = 'answer_quote_counter';
const ERROR_TEST_ID = 'answer-counter-error';
const DECLINE_REASON_MAX_LENGTH = 2000;
const AWAITING_PARTY = QuoteRoundParty.Seller;

type PriceKind = 'PICKUP' | 'DELIVERY' | 'TALLY_PICKUP' | 'TALLY_DELIVERY';

type CounterItem = {
  quoteItemId: string;
  platformProductLengthId: string;
  productId: string;
  productName: string;
  standardUnitsCode: UnitCode;
  lengthId: string;
  productLength: string;
  actualLengthInches: number;
  quantityInPieces: number;
  piecesPerBundle: number;
  offeredPickupPrice: number | null;
  offeredDeliveryPrice: number | null;
  ordered: boolean;
};

type ChangeLine = {
  platformProductLengthId: string | null;
  productName: string | null;
  productLength: string | null;
  kind: PriceKind;
  before: number | null;
  after: number | null;
  beforeCurrency: Currency | null;
  afterCurrency: Currency | null;
};

type QuantityChangeLine = {
  platformProductLengthId: string;
  productName: string;
  productLength: string;
  before: number | null;
  after: number | null;
};

type CounterData = {
  id: string;
  buyerOrganization: string;
  buyerFullName: string;
  sellerOrganization: string;
  shipFrom: string | null;
  shipTo: string | null;
  status: QuoteStatus;
  useTallyPrice: boolean;
  currency: Currency;
  leadTimeDays: number | null;
  pickupPriceForTally: number | null;
  deliveryPriceForTally: number | null;
  items: CounterItem[];
  changes: {
    lines: ChangeLine[];
    quantityLines: QuantityChangeLine[];
    leadTimeBefore: number | null;
    leadTimeAfter: number | null;
  };
};

type Outcome = 'accepted' | 'countered' | 'declined';

type Mode = 'choose' | 'accept' | 'decline' | 'counter';

type LineRow = {
  key: string;
  label: string;
  before: string;
  after: string;
  changed: boolean;
};

function fetchCounter(token: string): Promise<CounterData> {
  return getByToken<CounterData>(
    `/api/quotes/counter-by-token?token=${encodeURIComponent(token)}`
  );
}

function amountOf(money: MoneyInput | null | undefined): number | null {
  return money == null ? null : money.amount;
}

function moneyOf(amount: number | null, currency: Currency): Money | null {
  return amount == null ? null : { amount, currency };
}

function toCounterRequest(token: string, submission: CounterSubmission) {
  const { itemOverrides, tallyPrice, leadTimeDays } = submission;
  return {
    token,
    currency: tallyPrice.currency,
    useTallyPrice: tallyPrice.useTallyPrice,
    pickupPriceForTally: amountOf(tallyPrice.offeredPickupPriceForTally),
    deliveryPriceForTally: amountOf(tallyPrice.offeredDeliveryPriceForTally),
    leadTimeDays,
    itemOverrides: itemOverrides.map((override) => ({
      quoteItemId: override.quoteItemId,
      platformProductLengthId: override.platformProductLengthId,
      quantityInBundles: override.quantityInBundles,
      piecesPerBundle: override.piecesPerBundle,
      offeredPickupPrice: amountOf(override.offeredPickupPrice),
      offeredDeliveryPrice: amountOf(override.offeredDeliveryPrice),
      lengthId: null,
    })),
  };
}

function toGroupableItem(item: CounterItem): GroupableItem {
  return {
    quantityInPieces: item.quantityInPieces,
    piecesPerBundle: item.piecesPerBundle,
    platformProductsLength: {
      id: item.platformProductLengthId,
      length: {
        id: item.lengthId,
        displayValue: item.productLength,
        actualLengthInches: item.actualLengthInches,
      },
      platformProduct: {
        id: item.productId,
        name: item.productName,
        standardUnitsCode: item.standardUnitsCode,
        piecesPerBundle: {
          value: item.piecesPerBundle,
          unit: UnitCode.Pcs,
        },
      },
    },
  };
}

function toCounterableQuote(data: CounterData): CounterableQuote {
  return {
    id: data.id,
    status: data.status,
    negotiationAwaitingParty: AWAITING_PARTY,
    currency: data.currency,
    leadTimeDays: data.leadTimeDays,
    offeredPickupPriceForTally: moneyOf(
      data.pickupPriceForTally,
      data.currency
    ),
    offeredDeliveryPriceForTally: moneyOf(
      data.deliveryPriceForTally,
      data.currency
    ),
    items: data.items.map((item) => ({
      id: item.quoteItemId,
      quantityInPieces: item.quantityInPieces,
      piecesPerBundle: item.piecesPerBundle,
      offeredPickupPrice: moneyOf(item.offeredPickupPrice, data.currency),
      offeredDeliveryPrice: moneyOf(item.offeredDeliveryPrice, data.currency),
      platformProductsLength: { id: item.platformProductLengthId },
      ordered: item.ordered,
    })),
  };
}

export function AnswerQuoteCounter() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const load = useTokenLoadedOnce(token, fetchCounter);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [submitError, setSubmitError] = useState<TokenErrorCode | null>(null);
  const { t } = useTranslation(NAMESPACE);

  if (!token) {
    return (
      <TokenErrorView
        code="invalid"
        namespace={NAMESPACE}
        keyPrefix={KEY_PREFIX}
        testId={ERROR_TEST_ID}
      />
    );
  }

  if (load.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neut-bg-alt">
        <PageLoadingSpinner name={t('answer_quote_counter.loading')} />
      </div>
    );
  }

  if (outcome) {
    return <SuccessView outcome={outcome} />;
  }

  if (load.status === 'error' || submitError) {
    const code =
      submitError ?? (load.status === 'error' ? load.code : 'unknown');
    return (
      <TokenErrorView
        code={code}
        namespace={NAMESPACE}
        keyPrefix={KEY_PREFIX}
        testId={ERROR_TEST_ID}
      />
    );
  }

  return (
    <CounterAnswerForm
      data={load.data}
      token={token}
      onAnswered={setOutcome}
      onError={setSubmitError}
    />
  );
}

function CounterAnswerForm({
  data,
  token,
  onAnswered,
  onError,
}: Readonly<{
  data: CounterData;
  token: string;
  onAnswered: (outcome: Outcome) => void;
  onError: (code: TokenErrorCode) => void;
}>) {
  const { t } = useTranslation(NAMESPACE);
  const [mode, setMode] = useState<Mode>('choose');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const quote = useMemo(() => toCounterableQuote(data), [data]);
  const groups = useMemo(() => {
    const items = data.items.map(toGroupableItem);
    return buildProductGroups(items, items);
  }, [data.items]);
  const declinable = isDeclinable(quote);

  const answer = async (send: () => Promise<void>, result: Outcome) => {
    if (busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    try {
      await send();
      onAnswered(result);
    } catch (err) {
      busyRef.current = false;
      setBusy(false);
      const code = tokenErrorCodeOf(err);
      if (code === 'unknown') {
        toast.error(t('answer_quote_counter.error.submit'));
      } else {
        onError(code);
      }
    }
  };

  const acceptCounter = () =>
    answer(
      () => postByToken('/api/quotes/accept-counter-by-token', { token }),
      'accepted'
    );

  const declineQuote = () =>
    answer(
      () =>
        postByToken('/api/quotes/decline-counter-by-token', {
          token,
          reason: reason.trim() || null,
        }),
      'declined'
    );

  const submitCounter = (submission: CounterSubmission) =>
    answer(
      () =>
        postByToken(
          '/api/quotes/counter-by-token',
          toCounterRequest(token, submission)
        ),
      'countered'
    );

  const toggle = (next: Mode) =>
    setMode((current) => (current === next ? 'choose' : next));

  return (
    <div
      className="min-h-screen bg-neut-bg-alt py-8 px-4 w-full"
      data-testid="answer-counter-page"
    >
      <div className="max-w-3xl mx-auto flex flex-col gap-6">
        <header className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-[-0.01em] text-neut-prim">
            {t('answer_quote_counter.title', {
              buyerOrganization: data.buyerOrganization,
            })}
          </h1>
          <p className="text-sm text-neut-sec">
            {t('answer_quote_counter.subtitle', {
              buyerFullName: data.buyerFullName,
              sellerOrganization: data.sellerOrganization,
            })}
          </p>
          <dl
            className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm"
            data-testid="answer-counter-shipping"
          >
            <dt className="text-neut-sec">
              {t('answer_quote_counter.ship_from')}
            </dt>
            <dd className="text-neut-prim">{data.shipFrom ?? '—'}</dd>
            <dt className="text-neut-sec">
              {t('answer_quote_counter.ship_to')}
            </dt>
            <dd className="text-neut-prim">{data.shipTo ?? '—'}</dd>
          </dl>
        </header>

        <SectionPanel
          title={t('answer_quote_counter.counter.heading')}
          bodyClassName="overflow-x-auto"
        >
          <CounterLinesTable data={data} />
        </SectionPanel>

        {mode === 'accept' ? (
          <div className="flex flex-col gap-3 rounded-lg border border-neut-border p-4">
            <p className="text-sm text-neut-prim">
              {t('answer_quote_counter.actions.accept_question')}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="conversion"
                size="sm"
                width="auto"
                data-testid="answer-counter-accept-confirm"
                onClick={() => void acceptCounter()}
                loading={busy}
              >
                {t('answer_quote_counter.actions.accept')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                width="auto"
                data-testid="answer-counter-accept-cancel"
                onClick={() => setMode('choose')}
                disabled={busy}
              >
                {t('answer_quote_counter.actions.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="conversion"
              size="sm"
              width="auto"
              data-testid="answer-counter-accept"
              onClick={() => setMode('accept')}
              disabled={busy}
            >
              {t('answer_quote_counter.actions.accept')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              width="auto"
              data-testid="answer-counter-counter"
              aria-expanded={mode === 'counter'}
              onClick={() => toggle('counter')}
              disabled={busy}
            >
              {t('answer_quote_counter.actions.counter')}
            </Button>
            {declinable && (
              <Button
                variant="destructive-outline"
                size="sm"
                width="auto"
                data-testid="answer-counter-decline"
                aria-expanded={mode === 'decline'}
                onClick={() => toggle('decline')}
                disabled={busy}
              >
                {t('answer_quote_counter.actions.decline')}
              </Button>
            )}
          </div>
        )}

        {mode === 'counter' && (
          <div className="rounded-lg border border-neut-border p-4">
            <CounterQuoteForm
              quote={quote}
              groups={groups}
              usesWholeRequestPrice={data.useTallyPrice}
              requestForQuoteCurrency={data.currency}
              submitCounter={submitCounter}
            />
          </div>
        )}

        {mode === 'decline' && (
          <div className="flex flex-col gap-3 rounded-lg border border-neut-border p-4">
            <TextArea
              id="answer-counter-decline-reason"
              name="answer-counter-decline-reason"
              label={t('answer_quote_counter.actions.decline_reason')}
              value={reason}
              maxLength={DECLINE_REASON_MAX_LENGTH}
              rows={3}
              onChange={(_, value) => setReason(value)}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                variant="destructive"
                size="sm"
                width="auto"
                data-testid="answer-counter-decline-confirm"
                onClick={() => void declineQuote()}
                loading={busy}
              >
                {t('answer_quote_counter.actions.decline_confirm')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                width="auto"
                data-testid="answer-counter-decline-cancel"
                onClick={() => setMode('choose')}
                disabled={busy}
              >
                {t('answer_quote_counter.actions.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CounterLinesTable({ data }: Readonly<{ data: CounterData }>) {
  const { t } = useTranslation(NAMESPACE);
  const rows = counterLineRows(data, t);
  return (
    <table className="w-full text-sm" data-testid="answer-counter-lines">
      <thead>
        <tr className="border-b border-neut-border text-left text-xs text-neut-sec">
          <th className="px-4 py-2 font-semibold">
            {t('answer_quote_counter.counter.product')}
          </th>
          <th className="px-4 py-2 font-semibold">
            {t('answer_quote_counter.counter.your_offer')}
          </th>
          <th className="px-4 py-2 font-semibold">
            {t('answer_quote_counter.counter.counter')}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.key}
            className="border-b border-neut-border last:border-b-0"
          >
            <td className="px-4 py-2 text-neut-prim">{row.label}</td>
            <td className="px-4 py-2 text-neut-sec">
              {row.changed ? (
                <del className="line-through">{row.before}</del>
              ) : (
                row.before
              )}
            </td>
            <td className="px-4 py-2 text-neut-prim">
              {row.changed ? (
                <strong className="font-bold">{row.after}</strong>
              ) : (
                row.after
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const PRICE_KIND_KEYS: Record<PriceKind, string> = {
  PICKUP: 'answer_quote_counter.counter.pickup',
  DELIVERY: 'answer_quote_counter.counter.delivery',
  TALLY_PICKUP: 'answer_quote_counter.counter.tally_pickup',
  TALLY_DELIVERY: 'answer_quote_counter.counter.tally_delivery',
};

const ITEM_PRICE_KINDS = ['PICKUP', 'DELIVERY'] as const;

function counterLineRows(
  data: CounterData,
  t: (key: string, options?: Record<string, unknown>) => string
): LineRow[] {
  const price = (amount: number | null, currency: Currency | null = null) =>
    moneyOrDash(moneyOf(amount, currency ?? data.currency));
  const shownLines = new Set<ChangeLine>();
  const priceRow = (
    key: string,
    label: string,
    current: number | null,
    change: ChangeLine | undefined
  ): LineRow[] => {
    if (change === undefined && current == null) {
      return [];
    }
    if (change !== undefined) {
      shownLines.add(change);
    }
    return [
      {
        key,
        label,
        before:
          change === undefined
            ? price(current)
            : price(change.before, change.beforeCurrency),
        after:
          change === undefined
            ? price(current)
            : price(change.after, change.afterCurrency),
        changed: change !== undefined,
      },
    ];
  };

  const { lines, leadTimeBefore, leadTimeAfter } = data.changes;
  const tallyLine = (kind: PriceKind) =>
    lines.find((line) => line.kind === kind);
  const itemLine = (platformProductLengthId: string, kind: PriceKind) =>
    lines.find(
      (line) =>
        line.kind === kind &&
        line.platformProductLengthId === platformProductLengthId
    );

  const { quantityLines } = data.changes;
  const pieces = (count: number | null) =>
    count == null
      ? t('answer_quote_counter.counter.empty_value')
      : t('answer_quote_counter.counter.pieces', {
          pieces: formatQuantityForUnit({ value: count, unit: UnitCode.Pcs }),
        });
  const quantityRow = (line: QuantityChangeLine): LineRow => ({
    key: `${line.platformProductLengthId}-QUANTITY`,
    label: t('answer_quote_counter.counter.line_label', {
      product: line.productName,
      length: line.productLength,
      kind: t('answer_quote_counter.counter.quantity'),
    }),
    before: pieces(line.before),
    after: pieces(line.after),
    changed: true,
  });
  const quantityRowsOf = (platformProductLengthId: string) =>
    quantityLines
      .filter(
        (line) => line.platformProductLengthId === platformProductLengthId
      )
      .map(quantityRow);
  const itemLengthIds = new Set(
    data.items.map((item) => item.platformProductLengthId)
  );
  const removedQuantityRows = quantityLines
    .filter((line) => !itemLengthIds.has(line.platformProductLengthId))
    .map(quantityRow);

  const priceRows = data.useTallyPrice
    ? [
        ...priceRow(
          'TALLY_PICKUP',
          t(PRICE_KIND_KEYS.TALLY_PICKUP),
          data.pickupPriceForTally,
          tallyLine('TALLY_PICKUP')
        ),
        ...priceRow(
          'TALLY_DELIVERY',
          t(PRICE_KIND_KEYS.TALLY_DELIVERY),
          data.deliveryPriceForTally,
          tallyLine('TALLY_DELIVERY')
        ),
        ...data.items.flatMap((item) =>
          quantityRowsOf(item.platformProductLengthId)
        ),
      ]
    : data.items.flatMap((item) => [
        ...ITEM_PRICE_KINDS.flatMap((kind) =>
          priceRow(
            `${item.quoteItemId}-${kind}`,
            t('answer_quote_counter.counter.line_label', {
              product: item.productName,
              length: item.productLength,
              kind: t(PRICE_KIND_KEYS[kind]),
            }),
            kind === 'PICKUP'
              ? item.offeredPickupPrice
              : item.offeredDeliveryPrice,
            itemLine(item.platformProductLengthId, kind)
          )
        ),
        ...quantityRowsOf(item.platformProductLengthId),
      ]);

  const unmatchedPriceRows = lines
    .filter((line) => !shownLines.has(line))
    .flatMap((line) =>
      priceRow(
        `${line.platformProductLengthId ?? 'quote'}-${line.kind}`,
        line.platformProductLengthId == null
          ? t(PRICE_KIND_KEYS[line.kind])
          : t('answer_quote_counter.counter.line_label', {
              product: line.productName,
              length: line.productLength,
              kind: t(PRICE_KIND_KEYS[line.kind]),
            }),
        null,
        line
      )
    );

  const days = (count: number | null) =>
    count == null
      ? t('answer_quote_counter.counter.empty_value')
      : t('answer_quote_counter.counter.lead_time_days', { count });
  return [
    ...priceRows,
    ...unmatchedPriceRows,
    ...removedQuantityRows,
    {
      key: 'lead-time',
      label: t('answer_quote_counter.counter.lead_time'),
      before: days(leadTimeBefore),
      after: days(leadTimeAfter),
      changed: leadTimeBefore !== leadTimeAfter,
    },
  ];
}

function SuccessView({ outcome }: Readonly<{ outcome: Outcome }>) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <MagicLinkStatusCard
      testId="answer-counter-success"
      icon={<CheckCircle className="text-success-500" size={48} />}
      title={t(`answer_quote_counter.success.${outcome}_title`)}
      message={t('answer_quote_counter.success.message')}
    />
  );
}
